import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
// P5.2：路由切换过渡（全国态势 ↔ 项目星图）。仅定义令牌与动画，不重构页面结构。
import '../theme/motion.css';
import { useAuthStore } from '../stores/auth';
import { useUiStore } from '../stores/ui';
import { PROVINCE_NAME_BY_CODE } from '../features/map/regions';
import Clock from '../features/map/Clock';
// P6.55：用户菜单抽取为复用组件（普通页面 + 满屏指挥视图两处共用）
import UserMenu from '../components/UserMenu';
import TaskDrawer from '../features/detail/TaskDrawer';
// P6.2：新建任务改为右侧抽屉（与详情抽屉并列挂载，全应用常驻）
import TaskFormDrawer from '../features/task-form/TaskFormDrawer';

/** 导航项定义：路径 / 名称 / 图标 / 所需权限（任一满足即显示，否则整项不渲染） */
export interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** 所需权限点集合：任一满足即显示 */
  permissions: readonly string[];
}

/* P6.5：「全国态势」全站唯一，由面包屑承担入口职责，故从导航 / 侧栏移除。
   此处仅保留「系统设置」——它是唯一的导航项。
   P6.56：用户要求删除顶栏冗余的「系统设置」页签，本常量**仅供侧边栏渲染**；
   进入设置页改由「用户菜单 → 系统设置」与「侧边栏 → 系统设置」承担。 */
export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/settings',
    label: '系统设置',
    icon: '⚙',
    permissions: ['user:manage', 'role:manage', 'dict:manage', 'audit:view'],
  },
] as const;

/**
 * 应用外壳：顶栏 + 侧栏 + 内容区。
 *
 * ⚠️ 无权限的导航项**不渲染**（不是禁用）—— 用 hasAnyPermission 判断。
 *    超管恒 true。
 */
export default function AppShell() {
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * 全国态势页 / 项目星图页都是满屏指挥视图：隐藏与顶栏重复的侧栏导航，
   * 让地图 / 星图元素按原型以视口为基准定位（高保真原型本身没有侧栏）。
   *
   * ⚠️ P5.1：星图页对齐原型 #starv 全屏，顶栏右侧改为「时钟」
   *    （原型 #topright 没有搜索框 / 铃铛 / 头像）。
   */
  const fullBleed = location.pathname === '/map' || location.pathname === '/star';

  /**
   * P6.3：系统设置页隐藏左侧导航栏。
   *
   * 用户要求：「进入系统设置的时候，不需要左侧的导航栏」。
   *
   * ⚠️ 与 `fullBleed` **仍分开**：fullBleed 负责满屏指挥视图的
   *    `shell--full` 外壳与路由过渡遮罩；设置页只需隐藏侧栏。
   *    P6.58 起顶栏右侧已统一（同步徽标 + 时钟 + 用户菜单），
   *    两分支在顶栏内容上不再有差异，故此处只保留外壳/遮罩差异。
   *
   * 侧栏由 CSS `.shell--noaside .sidebar { display: none }` 隐藏
   * （见 global.css），与 `fullBleed` 的 `shell--full` 各自独立。
   */
  const isSettings = location.pathname.startsWith('/settings');
  const onStar = location.pathname === '/star';
  const provinceCode = useUiStore((s) => s.filters.provinceCode);
  const setFilter = useUiStore((s) => s.setFilter);
  /* P5.4：星图页项目信息 → 顶栏面包屑第三级（「全国态势 › 省 › 项目」） */
  const projectMeta = useUiStore((s) => s.currentProjectMeta);
  const setCurrentProjectId = useUiStore((s) => s.setCurrentProjectId);
  const clearCurrentProjectMeta = useUiStore((s) => s.clearCurrentProjectMeta);

  /* 无权限过滤：permissions 为空表示「无需任何权限」——所有登录用户可见。
     ⚠️ hasAnyPermission([]) 恒为 false（[].some），故必须单独放行空数组。
     P6.5：当前 NAV_ITEMS 已无空权限项，但该兜底逻辑语义正确，保留备用。 */
  const visible = NAV_ITEMS.filter(
    (item) => item.permissions.length === 0 || hasAnyPermission([...item.permissions]),
  );

  /* 面包屑数据：星图页取项目注册的省；地图页取筛选中的省。
     P5.6：面包屑最多两级（全国态势 › 省），不再取项目名 ——
     用户要求：星图页项目名已在页头显示，面包屑里重复显示是多余的。

     P6.4：省份名只在「地图 / 星图」页有意义（那是当前下钻的省）。
     其它页面（设置 / 审批 / 操作日志…）即便 uiStore 里残留 provinceCode，
     也不得显示 —— 否则会出现「全国态势 › 山东省」这种语义错误。 */
  const onMap = location.pathname === '/map';
  const showProvince = onMap || onStar;
  const crumbProvinceCode = showProvince
    ? ((onStar ? projectMeta.provinceCode : null) ?? provinceCode)
    : null;
  const crumbProvinceName = showProvince
    ? ((onStar ? projectMeta.provinceName : null) ??
      (crumbProvinceCode
        ? (PROVINCE_NAME_BY_CODE[crumbProvinceCode] ?? crumbProvinceCode)
        : null))
    : null;

  /** 面包屑「全国态势」：清空项目/省份，回全国视图 */
  const goHome = () => {
    setCurrentProjectId(null);
    clearCurrentProjectMeta();
    setFilter('provinceCode', undefined);
    navigate('/map');
  };

  /**
   * 面包屑省名（星图页）：回该省的地图下钻视图。
   *
   * ⚠️ P6.5：星图页省名**不是当前项**，对齐原型 renderCrumbs() 可点行为。
   *    故意**不**清空 `provinceCode` —— 星图来自地图下钻时该筛选仍然有效，
   *    保留它才能回到「该省已下钻」的地图视图（原型即如此）。
   *    仅清空当前项目，避免地图页残留项目上下文。
   */
  const goProvince = () => {
    setCurrentProjectId(null);
    clearCurrentProjectMeta();
    navigate('/map');
  };

  return (
    /* P6.64：取消整页等比缩放，改为流式自适应布局。
       外壳自身即为最外层（#root height:100%），顶栏固定高、侧栏固定宽、
       主内容区 flex:1 自适应；不再有 1920×1080 固定画布与 transform 缩放。 */
    <div className={fullBleed ? 'shell shell--full' : isSettings ? 'shell shell--noaside' : 'shell'}>
        <header className="topbar">
        <div className="topbar__brand">
          <span className="brandmark">
            <span>A</span>
          </span>
          <span className="txt">
            <b>ATLAS PM</b>
            <span>寰图 · 全国运营指挥</span>
          </span>
        </div>

        {/* 面包屑（组织层级：全国态势 → 省）——对齐原型 #crumbs：
            恒在顶栏内，以 flex:1 撑开中间空间。
            P5.4：曾把第三级（项目名）接入这里；P5.6 按用户要求**去掉**——
            进入星图后项目名已在星图页头显示，面包屑再显示一遍是重复的。
            故面包屑**最多两级**：全国态势 › 省。
            P6.56：顶栏「系统设置」页签已移除，面包屑现紧接 LOGO 之后，
            仍靠自身 flex:1 撑开中部空间（删去的是 flex:0 0 auto 的 nav，
            不影响面包屑与右侧区域的定位）。 */}
        <nav
          className="topbar__crumbs"
          aria-label="面包屑"
          style={{ flex: '1 1 auto', minWidth: 0 }}
        >
          {crumbProvinceName ? (
            <>
              <span className="c" onClick={goHome}>
                全国态势
              </span>
              <span className="sep">›</span>
              {onStar ? (
                /* P6.5：星图页省名不是当前项，可点回该省地图下钻（对齐原型） */
                <span className="c" onClick={goProvince}>
                  {crumbProvinceName}
                </span>
              ) : (
                /* 地图页下钻：省名即当前项，不可点 */
                <span className="c cur">{crumbProvinceName}</span>
              )}
            </>
          ) : onMap ? (
            /* 地图页即全国视图根，当前项不可点（保持原语义） */
            <span className="c cur">全国态势</span>
          ) : (
            /* 非地图页（设置 / 审批 / 操作日志…）——「全国态势」恒可点，
               点击回到 /map，保证任何页面都能回全国态势（P6.4 核心）。 */
            <span className="c" onClick={goHome}>
              全国态势
            </span>
          )}
        </nav>

        {/* P6.58：普通页面（设置 / 审批 / 操作日志…）与满屏指挥视图（全国态势 / 星图）
            统一顶栏右侧 —— 时钟 + 用户菜单。
            ⚠️ 原「搜索框 + 铃铛」是纯装饰（div/button 均无 onClick、快捷键从未实现），
               用户要求删除，故两分支合并为一处渲染，Clock 只挂载一份。
            ⚠️ P6.71：该同步徽标同为纯静态装饰（无交互、无数据绑定），
               用户要求删除，仅保留时钟 + 用户菜单。 */}
        <div className="topbar__right">
          <Clock />
          <UserMenu />
        </div>
      </header>

      <div className="shell__body">
        <aside className="sidebar">
          <div className="sidebar__label">导航</div>
          <nav className="sidebar__nav">
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => ['navlink', isActive ? 'on' : ''].join(' ')}
              >
                <span className="navlink__icon">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="shell__main">
          <Outlet />
        </main>
      </div>

      {/* P5.2 · 路由切换遮罩（仅满屏指挥视图 /map ↔ /star）：
          key 随路径变化 → 每次切换重新挂载，由不透明向透明淡出，
          在合成线程运行，遮蔽星图 Three.js 首帧初始化造成的主线程卡顿。 */}
      {fullBleed ? (
        <div className="p52-veil" key={location.pathname} aria-hidden="true" />
      ) : null}

      <TaskDrawer />
      <TaskFormDrawer />
    </div>
  );
}
